import { Store } from '@reduxjs/toolkit';
import {
  UsConsumptionEntryLine,
  UsType86EntryLine,
  UsPostSummaryCorrectionLine,
} from '@xbcb/api-gateway-client';
import { formatHTS } from '@xbcb/hts-client-utils';
import {
  ValidationErrorType,
  AppRecordValidateResult,
  NamePath,
} from '@xbcb/ui-types';
import {
  ComplianceCheck,
  ComplianceCheckStatus,
  ComplianceCheckType,
} from '__generated__/graphql';
import { getLatestComplianceCheck } from 'libs/complianceChecks';
import { section232AluminumRequirementsRequiredFieldsNamePaths } from 'components/AluminumSmeltAndCastCountryDetail/component';
import { section232CopperRequirementsRequiredFieldsNamePaths } from 'components/CopperSmeltAndCastCountryDetail/component';
import { NRBS_RECORD_NUMBER_LEN } from '../constant';
import {
  DEFAULT_EFFECTIVE_DATE,
  formatMomentAsEffectiveDateString,
} from 'libs/dateUtils';
import {
  isSection232AluminumSmeltAndCastApplicableForAnyTariff,
  isSection232CopperSmeltAndCastApplicableForAnyTariff,
  isSection232SteelMeltAndPourApplicableForAnyTariff,
} from 'libs/htsHelpers';
import moment from 'moment';
import {
  Section232SteelRequirementsFields,
  steelRequirementsFieldPrefix,
} from 'components/SteelMeltAndPourCountryDetail/component';

export type EntryLine =
  | UsConsumptionEntryLine
  | UsType86EntryLine
  | UsPostSummaryCorrectionLine;

export const validateNRBSNumber = (
  line: EntryLine,
  lineIndex: number,
): AppRecordValidateResult => {
  if (
    'nrbsRecordNumber' in line &&
    line?.nrbsRecordNumber &&
    line.nrbsRecordNumber.length !== NRBS_RECORD_NUMBER_LEN
  ) {
    return {
      additionalErrors: [
        {
          title: 'Nrbs Recording Number',
          type: ValidationErrorType.GENERAL,
          messages: [
            `Nrbs Record Number must be 10 alphanumeric characters in line ${
              lineIndex + 1
            }`,
          ],
        },
      ],
      validateFields: [],
    };
  } else return {};
};

// Blocks submission while a line's HTS reasonability check is FAILED. Accepting
// the recommendation or the manual-override checkbox moves it to
// USER_MANUAL_OVERRIDE, which clears the error.
export const validateReasonability = (
  line: EntryLine,
  lineIndex: number,
): AppRecordValidateResult => {
  const complianceChecks = (
    line as { complianceChecks?: ComplianceCheck[] | null }
  ).complianceChecks;
  const status = getLatestComplianceCheck(
    complianceChecks,
    ComplianceCheckType.REASONABILITY,
  )?.status;
  if (status === ComplianceCheckStatus.FAILED) {
    return {
      additionalErrors: [
        {
          title: 'HTS Reasonability Check Failed',
          type: ValidationErrorType.GENERAL,
          messages: [
            `Line ${
              lineIndex + 1
            } failed the HTS reasonability check. Accept the recommended HTS or select the manual override to proceed.`,
          ],
        },
      ],
      validateFields: [],
    };
  }
  return {};
};

export const validateSection232Aluminum = (
  line: EntryLine,
  lineNamePath: NamePath,
  arrivalDate?: moment.Moment,
  store?: Store,
  skipImportDateCheck?: boolean,
): AppRecordValidateResult => {
  const result: AppRecordValidateResult = { validateFields: [] };
  const storeState = store?.getState();
  const estimatedImportDateString = arrivalDate
    ? formatMomentAsEffectiveDateString(arrivalDate)
    : DEFAULT_EFFECTIVE_DATE;
  const effectiveDateHtsHierarchy =
    storeState?.hts?.usHtsData?.[estimatedImportDateString];
  const htsObjects =
    line.tariffs
      ?.filter((tariff) => tariff && tariff.htsNumber)
      ?.map(
        (tariff) =>
          effectiveDateHtsHierarchy?.[formatHTS(tariff.htsNumber)] ?? {},
      ) || [];

  if (
    isSection232AluminumSmeltAndCastApplicableForAnyTariff(
      htsObjects,
      arrivalDate?.toDate(),
      skipImportDateCheck,
    ) &&
    !line.doesNotContainAluminum
  ) {
    section232AluminumRequirementsRequiredFieldsNamePaths.forEach(
      (fieldNamePath) => {
        result.validateFields?.push([...lineNamePath, ...fieldNamePath]);
      },
    );
  }
  return result;
};

export const validateSection232Steel = (
  line: EntryLine,
  lineNamePath: NamePath,
  arrivalDate?: moment.Moment,
  store?: Store,
  skipImportDateCheck?: boolean,
): AppRecordValidateResult => {
  const result: AppRecordValidateResult = { validateFields: [] };
  const storeState = store?.getState();
  const estimatedImportDateString = arrivalDate
    ? formatMomentAsEffectiveDateString(arrivalDate)
    : DEFAULT_EFFECTIVE_DATE;
  const effectiveDateHtsHierarchy =
    storeState?.hts?.usHtsData?.[estimatedImportDateString];

  const htsObjects =
    line.tariffs
      ?.filter((tariff) => tariff && tariff.htsNumber)
      ?.map(
        (tariff) =>
          effectiveDateHtsHierarchy?.[formatHTS(tariff.htsNumber)] ?? {},
      ) || [];

  if (
    isSection232SteelMeltAndPourApplicableForAnyTariff(
      htsObjects,
      arrivalDate?.toDate(),
      skipImportDateCheck,
    ) &&
    !line.doesNotContainSteel
  ) {
    if (!line.steelRequirements?.derivativeSteelProductOfMexicoOnly) {
      result.validateFields?.push([
        ...lineNamePath,
        steelRequirementsFieldPrefix,
        Section232SteelRequirementsFields.STEEL_COUNTRY_OF_MELT_AND_POUR,
      ]);
    }
  }
  return result;
};

export const validateSection232Copper = (
  line: EntryLine,
  lineNamePath: NamePath,
  arrivalDate?: moment.Moment,
  store?: Store,
  skipImportDateCheck?: boolean,
): AppRecordValidateResult => {
  const result: AppRecordValidateResult = { validateFields: [] };
  const storeState = store?.getState();
  const estimatedImportDateString = arrivalDate
    ? formatMomentAsEffectiveDateString(arrivalDate)
    : DEFAULT_EFFECTIVE_DATE;
  const effectiveDateHtsHierarchy =
    storeState?.hts?.usHtsData?.[estimatedImportDateString];

  const htsObjects =
    line.tariffs
      ?.filter((tariff) => tariff && tariff.htsNumber)
      ?.map(
        (tariff) =>
          effectiveDateHtsHierarchy?.[formatHTS(tariff.htsNumber)] ?? {},
      ) || [];

  if (
    isSection232CopperSmeltAndCastApplicableForAnyTariff(
      htsObjects,
      arrivalDate?.toDate(),
      skipImportDateCheck,
    ) &&
    !('doesNotContainCopper' in line && line.doesNotContainCopper)
  ) {
    section232CopperRequirementsRequiredFieldsNamePaths.forEach(
      (fieldNamePath) => {
        result.validateFields?.push([...lineNamePath, ...fieldNamePath]);
      },
    );
  }
  return result;
};
